import { useState } from 'react'
import { useBreadcrumb } from '../hooks/useBreadcrumb'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { useForm } from 'react-hook-form'
import toast from 'react-hot-toast'
import { FaExclamationTriangle, FaPlus, FaEye } from 'react-icons/fa'
import { issueService } from '../services'
import { Card, PageHeader, StatusBadge, LoadingOverlay, EmptyState, Button, Input, Select, MobileCard, Modal } from '../components/common'
import { formatDateTime } from '../utils/datetime'
import { useAuth } from '../hooks/useAuth'

const categoryOptions = [
  { value: '货物问题', label: '货物问题' },
  { value: '物流问题', label: '物流问题' },
  { value: '账户问题', label: '账户问题' },
  { value: '费用问题', label: '费用问题' },
  { value: '其他', label: '其他' },
]

export default function IssuesPage() {
  const breadcrumb = useBreadcrumb()
  const queryClient = useQueryClient()
  const [showModal, setShowModal] = useState(false)
  const [isSubmitting, setIsSubmitting] = useState(false)

  const { isAuthenticated } = useAuth()

  const { register, handleSubmit, reset, formState: { errors } } = useForm()

  /*
   * enabled: isAuthenticated —— 访客态不能发这个请求。
   *
   * 路由层刻意放行游客（访客模式：看结构 + 引导登录，见 ProtectedRoute 注释），
   * 而 api.js 拦到 401 会把人踢去登录页并弹「登录已过期」——对从未登录的游客这话是错的，
   * 页面还会先闪一下再跳走。其余受保护页都写了这个守卫，本页原先漏了。
   * 见 src/test/guest-mode.test.jsx。
   */
  const { data: issues, isLoading } = useQuery({
    queryKey: ['issues'],
    queryFn: () => issueService.getIssues({ limit: 20 }),
    enabled: isAuthenticated,
  })

  const issueRecords = Array.isArray(issues) ? issues : (issues?.records || [])

  // Create issue mutation
  const createMutation = useMutation({
    mutationFn: issueService.createIssue,
    onSuccess: () => {
      toast.success('问题反馈已提交')
      reset()
      setShowModal(false)
      queryClient.invalidateQueries({ queryKey: ['issues'] })
    },
    onError: (error) => {
      toast.error(error.message || '提交失败')
    },
  })

  const onSubmit = async (data) => {
    setIsSubmitting(true)
    try {
      await createMutation.mutateAsync({
        title: data.title,
        category: data.category,
        description: data.description,
        relatedBookingId: data.relatedBooking ? Number(data.relatedBooking) : null,
      })
    } finally {
      setIsSubmitting(false)
    }
  }

  const priorityColors = {
    'High': 'text-red-600 bg-red-100',
    'Medium': 'text-yellow-600 bg-yellow-100',
    'Low': 'text-gray-600 bg-gray-100',
  }

  return (
    <div>
      <PageHeader
        icon={FaExclamationTriangle}
        title="问题反馈"
        subtitle="提交问题，获得帮助"
        breadcrumb={breadcrumb}
      />

      <div className="container py-8">
        {/* Create Button */}
        <div className="flex justify-end mb-6">
          <Button icon={<FaPlus />} onClick={() => setShowModal(true)}>
            提交问题
          </Button>
        </div>

        {/* Issues List */}
        <Card>
          <Card.Header>
            <h5 className="font-semibold">我的问题记录</h5>
          </Card.Header>
          <Card.Body className="p-0">
            {isLoading ? (
              <LoadingOverlay />
            ) : issueRecords.length > 0 ? (
              <>
              {/* 小屏卡片视图 */}
              <div className="md:hidden space-y-3 p-4">
                {issueRecords.map((issue) => (
                  <MobileCard
                    key={issue.id}
                    title={issue.title}
                    subtitle={[issue.issueCode || issue.id, issue.category].filter(Boolean).join('  ·  ')}
                    badge={<StatusBadge status={issue.status} />}
                    fields={[
                      {
                        label: '优先级',
                        value: (
                          <span className={`px-2 py-0.5 rounded text-xs font-medium ${priorityColors[issue.priority] || 'text-gray-600 bg-gray-100'}`}>
                            {issue.priority || 'Medium'}
                          </span>
                        ),
                      },
                      { label: '提交时间', value: formatDateTime(issue.createTime, 'MM-DD HH:mm') },
                    ]}
                  />
                ))}
              </div>

              <div className="hidden md:block overflow-x-auto">
                <table className="table">
                  <thead>
                    <tr>
                      <th>问题编号</th>
                      <th>标题</th>
                      <th>分类</th>
                      <th>优先级</th>
                      <th>状态</th>
                      <th>提交时间</th>
                      <th>操作</th>
                    </tr>
                  </thead>
                  <tbody>
                    {issueRecords.map((issue) => (
                      <tr key={issue.id}>
                        <td className="font-medium">{issue.issueCode || issue.id}</td>
                        <td>{issue.title}</td>
                        <td className="text-gray-600">{issue.category}</td>
                        <td>
                          <span className={`px-2 py-0.5 rounded text-xs font-medium ${priorityColors[issue.priority] || 'text-gray-600 bg-gray-100'}`}>
                            {issue.priority || 'Medium'}
                          </span>
                        </td>
                        <td><StatusBadge status={issue.status} /></td>
                        <td className="text-gray-500 text-sm">
                          {formatDateTime(issue.createTime, 'MM-DD HH:mm')}
                        </td>
                        <td>
                          <button className="text-primary-600 hover:text-primary-700">
                            <FaEye />
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              </>
            ) : (
              <EmptyState
                title="暂无问题记录"
                description="如有任何问题，欢迎反馈给我们"
                actionLabel="提交问题"
                action={() => setShowModal(true)}
              />
            )}
          </Card.Body>
        </Card>
      </div>

      {/* Create Issue Modal */}
      <Modal open={showModal} onClose={() => setShowModal(false)} size="lg" title="提交问题反馈">
            <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
              <Input
                label="问题标题"
                placeholder="简要描述您的问题"
                required
                error={errors.title?.message}
                {...register('title', { required: '请输入问题标题' })}
              />

              <Select
                label="问题分类"
                required
                placeholder="请选择问题分类"
                options={categoryOptions}
                error={errors.category?.message}
                {...register('category', { required: '请选择问题分类' })}
              />

              <Input
                label="关联单号"
                placeholder="相关的报名单号/运单号（可选）"
                {...register('relatedBooking')}
              />

              <div>
                <label className="form-label">问题描述 <span className="text-red-500">*</span></label>
                <textarea
                  className="form-control"
                  rows={4}
                  placeholder="请详细描述您遇到的问题..."
                  {...register('description', { required: '请描述问题详情' })}
                />
                {errors.description && (
                  <p className="mt-1 text-sm text-red-500">{errors.description.message}</p>
                )}
              </div>

              {/* 小屏按钮撑满并上下排 */}
              <div className="flex flex-col-reverse sm:flex-row sm:justify-end gap-2 sm:gap-3 pt-4">
                <Button variant="secondary" onClick={() => setShowModal(false)} className="w-full sm:w-auto">
                  取消
                </Button>
                <Button type="submit" loading={isSubmitting} className="w-full sm:w-auto">
                  提交
                </Button>
              </div>
            </form>
      </Modal>
    </div>
  )
}
